import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { NotificationService } from '@shared/services/notification.service';
import { PermissionExpressionService } from '@shared/services/permission-expression.service';
import { UserPermissionsService } from '@shared/services/userpermissions.service';
import { IWorktask, WorktaskService } from '@shared/services/worktask.service';
import { UUID } from 'node:crypto';
import { Subject, takeUntil } from 'rxjs';
import { IShiftListViewModel, ITimestampViewModel } from './mes-shift-approval.component';
import { ITimestamp, MesShiftService } from './mes-shift.service';
import { doesOverlapOff, resolveTimeToDate } from './shift-off-displacement.utils';

export interface IActivityView {
	id: string;
	name: string;
}

export interface ICheckTimeView {
	activity: IActivityView;
	startTime: number;
	endTime: number;
}

@Component({
	selector: 'app-shift-checktime-list',
	templateUrl: './shift-checktime-list.component.html',
	styleUrl: './shift-checktime-list.component.scss',
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class ShiftChecktimeListComponent implements OnInit, OnDestroy {
	private destroy$: Subject<void> = new Subject<void>();

	displayedColumns: string[] = ['activity', 'startTime', 'endTime', 'commands'];

	inputColumns: string[] = ['activity-input', 'startTime-input', 'endTime-input', 'commands-input'];

	dataSource = new MatTableDataSource<ITimestampViewModel>();
	showEmptyRow = true;
	timeStampBeingEdited = null;
	startTimeError = false;
	approved = false;
	newTask = { worktaskId: '', startTime: '', endTime: '' };
	editTask = { worktaskId: '', startTime: '' };

	protected hasUpdateAndCreatePermission = false;
	protected hasUpdateOrDeletePermission = false;
	protected hasUpdatePermission = false;

	@Input() shiftData: IShiftListViewModel;
	@Input() worktasks: IWorktask[];

	constructor(
		private shiftservice: MesShiftService,
		private notificationService: NotificationService,
		private permissionExpressionService: PermissionExpressionService,
		private userPermissionsService: UserPermissionsService,
	) {}

	ngOnInit(): void {
		this.dataSource.data = this.shiftData.timeStamps;
		this.approved = this.shiftData.approved;

		this.userPermissionsService.permissions$.pipe(takeUntil(this.destroy$)).subscribe(() => {
			this.hasUpdateAndCreatePermission = this.permissionExpressionService.hasPermission(['mes:ShiftUpdate', 'mes:ShiftCreate']);
			this.hasUpdateOrDeletePermission = this.permissionExpressionService.hasPermission(['mes:ShiftUpdate|mes:ShiftDelete']);
			this.hasUpdatePermission = this.permissionExpressionService.hasPermission('mes:ShiftUpdate');
		});
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	isNewValid() {
		return this.newTask.worktaskId === '' || this.newTask.startTime === '';
	}

	mapAndChainTimeStamps(timeStamps: ITimestamp[]): ITimestampViewModel[] {
		const timeStampView = new Array<ITimestampViewModel>();
		for (let i = 0; i < timeStamps.length; i++) {
			timeStampView.push(<ITimestampViewModel>{
				dateTimeOffset: timeStamps[i].dateTimeOffset,
				worktask: this.worktasks.find((c) => c.id === timeStamps[i].workTaskId),
				timestampStatus: timeStamps[i].timestampStatus ?? 'None',
			});
		}
		for (let i = 0; i < timeStampView.length; i++) {
			timeStampView[i].nextTimeStamp = timeStampView[i + 1];
		}
		return timeStampView;
	}

	removeTimeStamp(element: ITimestampViewModel): void {
		if (!element?.worktask?.id || element.worktask.id === WorktaskService.offWorkTaskId) {
			return;
		}
		const shift = this.shiftData.item;
		const deleteShift = (element.nextTimeStamp?.worktask?.id === WorktaskService.offWorkTaskId && shift.timestamps.length === 2) || shift.timestamps.length === 1;
		if (deleteShift) {
			this.shiftservice.deleteShift(shift).subscribe();
			return;
		}
		shift.timestamps = shift.timestamps.filter((e) => e.id !== element.id);
		this.shiftservice.updateShift(shift).subscribe();
	}

	editTimeStamp(timeStamp: ITimestampViewModel) {
		this.timeStampBeingEdited = timeStamp;
		const startDate = new Date(timeStamp.dateTimeOffset);
		this.editTask = {
			...this.editTask,
			startTime: `${startDate.getHours().toString().padStart(2, '0')}:${startDate.getMinutes().toString().padStart(2, '0')}`,
			worktaskId: timeStamp.worktask.id,
		};
	}

	saveTimeStamp(timeStamp: ITimestampViewModel) {
		const originalShift: IShiftListViewModel = JSON.parse(JSON.stringify(this.shiftData)) as IShiftListViewModel;
		const shift = this.shiftData.item;
		const sortedTimestamps = [...shift.timestamps].sort((a, b) => new Date(a.dateTimeOffset).getTime() - new Date(b.dateTimeOffset).getTime());
		const lastTimestampOnShift = sortedTimestamps[sortedTimestamps.length - 1];
		const hasAndNotIsOffstamp = timeStamp.worktask.id !== WorktaskService.offWorkTaskId && sortedTimestamps.some((e) => e.workTaskId === WorktaskService.offWorkTaskId);
		timeStamp.worktask = this.worktasks.find((wt) => wt.id === this.editTask.worktaskId);
		timeStamp.dateTimeOffset = this.getDateTimeOffsetString(
			this.editTask.startTime,
			lastTimestampOnShift.dateTimeOffset,
			hasAndNotIsOffstamp, // Has offstamp and not is offstamp = move day back if necessary
		);

		const idx = shift.timestamps.findIndex((ts) => ts.id === timeStamp.id);
		if (idx > -1) {
			shift.timestamps[idx] = {
				...shift.timestamps[idx],
				dateTimeOffset: timeStamp.dateTimeOffset,
				workTaskId: timeStamp.worktask.id as UUID,
				timestampStatus: this.isOfftask(timeStamp.worktask) ? 'None' : shift.timestamps[idx].timestampStatus,
			};
		} else {
			return;
		}

		this.shiftservice.updateShift(shift).subscribe({
			next: (response) => {
				this.dataSource.data = this.mapAndChainTimeStamps(response.result.timestamps);
			},
			error: (response) => {
				this.dataSource.data = originalShift.timeStamps;
				this.shiftData = originalShift;
				this.timestampNotifyError('update', 'interposed-exception', response.error.error.message);
			},
		});

		this.stopEditTimeStamp();
	}

	stopEditTimeStamp() {
		this.startTimeError = false;
		this.timeStampBeingEdited = null;
	}

	isNewStartTimeValid(timeStamp: ITimestampViewModel) {
		const startTime = new Date(timeStamp.dateTimeOffset);
		const [hours, minutes] = this.editTask.startTime.split(':').map(Number);
		startTime.setHours(hours, minutes, 0, 0);

		if (this.isOfftask(timeStamp.worktask)) {
			// If we're editing an offStamp, the new time is not allowed to be before the last of the other timestamps
			const lastTimeStamp = this.shiftData.timeStamps
				.filter((ts) => !this.isOfftask(ts.worktask))
				.reduce((latest, current) => {
					return !latest || new Date(current.dateTimeOffset) > new Date(latest.dateTimeOffset) ? current : latest;
				});

			if (startTime <= new Date(lastTimeStamp.dateTimeOffset)) {
				this.startTimeError = true;
				return;
			}
		} else {
			// If not, the new time is not allowed to be the same as the offstamp
			const offStamp = this.shiftData.timeStamps.find((ts) => this.isOfftask(ts.worktask));
			const offStampTime = new Date(offStamp.dateTimeOffset);
			if (startTime.getHours() === offStampTime.getHours() && startTime.getMinutes() === offStampTime.getMinutes()) {
				this.startTimeError = true;
				return;
			}
		}
		this.startTimeError = false;
	}

	isOfftask(worktask: IWorktask): boolean {
		return worktask?.id === WorktaskService.offWorkTaskId;
	}

	isAutoAdded(element: ITimestampViewModel): boolean {
		return this.isOfftask(element.worktask) && element.timestampStatus === 'AutoAdded';
	}

	acknowledgeAutoAdded(element: ITimestampViewModel): void {
		const shift = this.shiftData.item;
		const idx = shift.timestamps.findIndex((ts) => ts.id === element.id);
		if (idx > -1) {
			shift.timestamps[idx] = {
				...shift.timestamps[idx],
				timestampStatus: 'None',
			};
			this.shiftservice.updateShift(shift).subscribe({
				next: (response) => {
					this.dataSource.data = this.mapAndChainTimeStamps(response.result.timestamps);
					element.timestampStatus = 'None';
				},
				error: (response) => {
					this.timestampNotifyError('update', 'interposed-exception', response.error?.error?.message);
				},
			});
		}
	}

	addWorktask() {
		// In case something goes wrong, revert back to original shift
		const originalShift: IShiftListViewModel = JSON.parse(JSON.stringify(this.shiftData)) as IShiftListViewModel;
		// Copy into new array to ensure that timestamps on the shift aren't sorted by this function
		const sortedTimestamps = [...this.shiftData.item.timestamps].sort((a, b) => new Date(a.dateTimeOffset).getTime() - new Date(b.dateTimeOffset).getTime());
		// The last timestamp on our shift
		const lastTimestampOnShift = sortedTimestamps[sortedTimestamps.length - 1];
		// We cannot create an interjected timestamp with worktasktype offwork
		const newIsOfftask = this.newTask.worktaskId === WorktaskService.offWorkTaskId;
		if (newIsOfftask && this.newTask.endTime) {
			this.timestampNotifyError('create', 'interposed-end-is-not-possible');
			return;
		}
		// We cannot create an offtask on a shift that already has an offtask
		const hasOffstamp = sortedTimestamps.some((e) => e.workTaskId === WorktaskService.offWorkTaskId);
		if (newIsOfftask && hasOffstamp) {
			this.timestampNotifyError('create', 'interposed-end-already-exists');
			return;
		}
		// Determine if this is an Off-displacement case:
		// User enters an end time that is numerically after the Off time (in local hours/minutes)
		const offTimestampEntry = hasOffstamp ? sortedTimestamps.find((e) => e.workTaskId === WorktaskService.offWorkTaskId) : null;
		const isDisplacementCase = this.isOffDisplacementCase(offTimestampEntry, lastTimestampOnShift);

		// Get the datetime on when our new timestamp should be active
		const toCreateTimestampStart = this.getDateTimeOffsetString(this.newTask.startTime, lastTimestampOnShift.dateTimeOffset, hasOffstamp);
		// Timestamp cannot have same date and time as existing timestamp on shift
		// Exception: in displacement case, start time can equal Off time (Off will be moved)
		const startOverlapsExisting = sortedTimestamps.some((e) => new Date(e.dateTimeOffset).getTime() === new Date(toCreateTimestampStart).getTime());
		if (startOverlapsExisting) {
			const overlapsOff = isDisplacementCase && offTimestampEntry && new Date(toCreateTimestampStart).getTime() === new Date(offTimestampEntry.dateTimeOffset).getTime();
			if (!overlapsOff) {
				this.timestampNotifyError('create', 'start-overlap');
				return;
			}
		}
		// Placeholder timestamp
		const placeholder: ITimestamp = {
			id: null,
			dateTimeOffset: null,
			departmentId: lastTimestampOnShift.departmentId,
			employeeId: lastTimestampOnShift.employeeId,
			shiftId: lastTimestampOnShift.shiftId,
			workTaskId: null,
			teamProfileId: lastTimestampOnShift.teamProfileId,
		};
		// When we try to create an interposed timestamp
		if (this.newTask.endTime) {
			// Get the datetime on when previous timestamp should be active again
			const toCreateTimestampEnd = this.getDateTimeOffsetString(this.newTask.endTime, lastTimestampOnShift.dateTimeOffset, isDisplacementCase ? false : hasOffstamp);
			// The new timestamps time must be before the time we wish to go back to the original timestamp
			if (new Date(toCreateTimestampEnd).getTime() <= new Date(toCreateTimestampStart).getTime()) {
				this.timestampNotifyError('create', 'interposed-end-before-start');
				return;
			}

			// Check if the new range overlaps with the Off timestamp
			const offTimestamp = offTimestampEntry;
			if (offTimestamp && doesOverlapOff(new Date(toCreateTimestampStart), new Date(toCreateTimestampEnd), new Date(offTimestamp.dateTimeOffset))) {
				// Find the timestamp that was active before the new start time
				const previousTimestamp = [...sortedTimestamps].reverse().find((ts) => new Date(ts.dateTimeOffset).getTime() < new Date(toCreateTimestampStart).getTime());
				// Cannot insert before the start of the shift
				if (!previousTimestamp) {
					this.timestampNotifyError('create', 'interposed-start-before-start');
					return;
				}
				// Cannot start working on something already being worked on
				if (previousTimestamp.workTaskId === this.newTask.worktaskId) {
					this.timestampNotifyError('create', 'interposed-worktask-identical');
					return;
				}
				// Reject if any non-Off timestamp falls within [start, end]
				const nonOffOverlap = sortedTimestamps.some((ts) => ts.workTaskId !== WorktaskService.offWorkTaskId && new Date(toCreateTimestampStart).getTime() <= new Date(ts.dateTimeOffset).getTime() && new Date(toCreateTimestampEnd).getTime() >= new Date(ts.dateTimeOffset).getTime());
				if (nonOffOverlap) {
					this.timestampNotifyError('create', 'interposed-overlap');
					return;
				}
				// Displace Off timestamp forward to the end time
				offTimestamp.dateTimeOffset = toCreateTimestampEnd;
			} else {
				// Standard interposed logic: validate and insert "go back to previous task" timestamp
				// We cannot insert a timestamp after the end of a shift.
				if (new Date(toCreateTimestampEnd).getTime() >= new Date(lastTimestampOnShift.dateTimeOffset).getTime()) {
					this.timestampNotifyError('create', 'interposed-end-after-shift');
					return;
				}
				// Find the timestamp that we should go back to after the new timestamp
				const previousTimestampOnShift = [...sortedTimestamps].reverse().find((ts) => new Date(ts.dateTimeOffset).getTime() < new Date(toCreateTimestampStart).getTime());
				// We cannot insert a timestamp before the start of a shift if we want to go back to the previous task.
				if (!previousTimestampOnShift) {
					this.timestampNotifyError('create', 'interposed-start-before-start');
					return;
				}
				// We cannot start working on something that is already being worked on
				if (previousTimestampOnShift.workTaskId === this.newTask.worktaskId) {
					this.timestampNotifyError('create', 'interposed-worktask-identical');
					return;
				}
				// Find overlapping timestamps, cannot be equal either
				const overlaps = this.shiftData.item.timestamps.some((ts) => new Date(toCreateTimestampStart).getTime() <= new Date(ts.dateTimeOffset).getTime() && new Date(toCreateTimestampEnd).getTime() >= new Date(ts.dateTimeOffset).getTime());
				// We cannot insert overlapping timestamps.
				if (overlaps) {
					this.timestampNotifyError('create', 'interposed-overlap');
					return;
				}
				// Insert the original timestamp after the new timestamp to go back to the original worktask.
				this.shiftData.item.timestamps.unshift({
					...placeholder,
					workTaskId: previousTimestampOnShift.workTaskId,
					dateTimeOffset: toCreateTimestampEnd,
				});
			}
		}
		// Inser the new timestamp to start working on the new worktask
		this.shiftData.item.timestamps.unshift({
			...placeholder,
			workTaskId: this.newTask.worktaskId as UUID,
			dateTimeOffset: toCreateTimestampStart,
		});
		// Reset the newTask so we can begin to create new timestamps again
		this.newTask = { worktaskId: '', startTime: '', endTime: '' };
		// Update the shift with the new timestamp(s) and map the new timestamps.
		this.shiftservice.updateShift(this.shiftData.item).subscribe({
			next: (response) => {
				// Update shift succeeded, map the new timestamps
				this.dataSource.data = this.mapAndChainTimeStamps(response.result.timestamps);
			},
			error: (response) => {
				// Update shift failed, revert back to original shift
				this.dataSource.data = originalShift.timeStamps;
				this.shiftData = originalShift;
				this.timestampNotifyError('create', 'interposed-exception', response.error.error.message);
			},
		});
	}

	timestampNotifyError(method: string, error: string, message: string = '') {
		this.notificationService.notify([`mes.administration.shift-approval.table.timestamps-table.errors.${method}-timestamp.heading`], [`mes.administration.shift-approval.table.timestamps-table.errors.${method}-timestamp.errors.${error}`, { content: message }], 'error');
	}

	// Get the time of day based on the end of the shift
	getDateTimeOffsetString(newTimestampTime: string, lastTimestamp: string, hasOffstamp: boolean = true): string {
		return resolveTimeToDate(newTimestampTime, lastTimestamp, hasOffstamp);
	}

	/** Determines if the current insert operation is an Off-displacement case. This is true when the user provides an end time AND that end time would resolve to after the Off timestamp (meaning they want to push Off forward). */
	private isOffDisplacementCase(offTimestamp: ITimestamp | null, lastTimestamp: ITimestamp): boolean {
		if (!offTimestamp || !this.newTask.endTime) return false;
		// Resolve end time WITHOUT day subtraction and check if it's after Off
		const endResolved = resolveTimeToDate(this.newTask.endTime, lastTimestamp.dateTimeOffset, false);
		return new Date(endResolved).getTime() > new Date(offTimestamp.dateTimeOffset).getTime();
	}
}
